import { jwtDecode } from 'jwt-decode';
import React, { createContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';

export const AppContext = createContext();

export const AppProvider = ({ children }) => {
    const [userData, setUserData] = useState(null);
    const [user, setUser] = useState(null);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [startAuth, setStartAuth] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [discourseEntryPoint, setDiscourseEntryPoint] = useState(false);
    const [authStep, setAuthStep] = useState('login'); // 'login', 'signup', 'accountType', 'resetPassword'

    // Check URL for token and is_new_user params on component mount
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');
        const isNewUser = urlParams.get('is_new_user') === 'true';

        if (token) {
            try {
                const decodedToken = jwtDecode(token);
                console.log('Decoded Token:', decodedToken);

                // Check if the token has an expiration (exp) field
                if (!decodedToken.exp) {
                    console.warn('Token does not have an expiration field (exp).');
                } else {
                    console.log('Token Expiration:', new Date(decodedToken.exp * 1000));
                }

                // Create userData object from the decoded token
                const userDataObj = {
                    user_data: {
                        ...decodedToken.user_data, // Assuming user_data is nested in the token
                        token: token // Include the raw token in user_data
                    }
                };

                // Store token and userData in localStorage
                localStorage.setItem('token', token);
                localStorage.setItem('userData', JSON.stringify(userDataObj));

                // Update state
                setUserData(userDataObj);
                setUser(decodedToken.user_data);
                setIsAuthenticated(true);

                // Handle new user flow
                if (isNewUser) {
                    setStartAuth(true);
                    setAuthStep('accountType');
                } else {
                    window.location.href = '/vetting';
                }

                // Clean up URL
                window.history.replaceState({}, document.title, window.location.pathname);
            } catch (error) {
                console.error('Error decoding or validating token:', error);
                localStorage.removeItem('token');
                localStorage.removeItem('userData');
                setIsAuthenticated(false);
                setStartAuth(true);
                setAuthStep('login');
                toast.error('Session expired. Please log in again.');
            }
        }
    }, []);

    // Load userData from localStorage on initial render
    useEffect(() => {
        const storedUserData = localStorage.getItem('userData');
        if (storedUserData) {
            try {
                const parsedData = JSON.parse(storedUserData);
                console.log('Loading userData from localStorage:', parsedData);
                
                // Check if role exists and is properly set
                const userDataRole = parsedData.user_data?.role;
                console.log('User role from localStorage:', userDataRole);
                
                // Ensure role is properly set if it doesn't exist but user has admin privileges
                if (parsedData.user_data) {
                    const isStaff = parsedData.user_data.is_staff;
                    const isSuperuser = parsedData.user_data.is_superuser;
                    console.log('is_staff:', isStaff, 'is_superuser:', isSuperuser);
                    
                    // Set admin role if either is_staff or is_superuser is true
                    if (!parsedData.user_data.role && (isStaff || isSuperuser)) {
                        console.log('Setting admin role based on admin privileges');
                        parsedData.user_data.role = 'admin';
                        
                        // Save the updated data back to localStorage
                        localStorage.setItem('userData', JSON.stringify(parsedData));
                    }
                }
                
                console.log('Final userData with role:', parsedData);
                setUserData(parsedData);
                setUser(parsedData.user_data);
                setIsAuthenticated(true);
            } catch (error) {
                console.error('Error parsing userData from localStorage:', error);
                localStorage.removeItem('userData');
            }
        }
    }, []);

    // Update localStorage when userData changes
    useEffect(() => {
        if (userData) {
            localStorage.setItem('userData', JSON.stringify(userData));
            setIsAuthenticated(true);
        } else {
            localStorage.removeItem('userData');
            setIsAuthenticated(false);
        }
    }, [userData]);

    // Handle beforeunload event to save userData
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (userData) {
                localStorage.setItem('userData', JSON.stringify(userData));
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [userData]);

    const userLogin = async (loginData) => {
        setLoading(true);
        const LOG_IN_ENDPOINT = 'https://unido-api.sustainable.co.ke/accounts/login/';

        try {
            const res = await fetch(LOG_IN_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(loginData),
            });

            if (!res.ok) {
                setLoading(false);
                
                // Parse error response
                let errorData;
                try {
                    errorData = await res.json();
                } catch (parseError) {
                    console.error('Failed to parse error response:', parseError);
                    errorData = {};
                }

                // Handle different error types
                let userFriendlyMessage = '';
                
                if (res.status === 401) {
                    // Authentication failed - wrong credentials
                    userFriendlyMessage = 'Invalid email or password. Please check your credentials and try again.';
                } else if (res.status === 400) {
                    // Bad request - malformed data
                    userFriendlyMessage = 'Please check your email and password format and try again.';
                } else if (res.status === 429) {
                    // Too many requests
                    userFriendlyMessage = 'Too many login attempts. Please wait a few minutes before trying again.';
                } else if (res.status >= 500) {
                    // Server error
                    userFriendlyMessage = 'Our servers are experiencing issues. Please try again in a few minutes.';
                } else {
                    // Generic error
                    userFriendlyMessage = errorData.error || 'Login failed. Please try again.';
                }

                toast.error(userFriendlyMessage);
                
                // Create an error object with additional context
                const enhancedError = new Error(userFriendlyMessage);
                enhancedError.status = res.status;
                enhancedError.isAuthError = res.status === 401;
                enhancedError.originalError = errorData;
                
                throw enhancedError;
            }

            const response = await res.json();
            console.log('Login response is ', response);
            
            // Ensure role is set based on privileges
            if (response.user_data) {
                if (!response.user_data.role) {
                    if (response.user_data.is_staff || response.user_data.is_superuser) {
                        console.log('Setting admin role during login based on privileges');
                        response.user_data.role = 'admin';
                    } else {
                        response.user_data.role = 'user';
                    }
                }
            }
            
            setUserData(response);
            setUser(response.user_data);
            setIsAuthenticated(true);
            
            // Store token
            if (response.user_data && response.user_data.token) {
                localStorage.setItem('token', response.user_data.token);
            }
            
            toast.success('Login Successful');

            if (discourseEntryPoint) {
                const { sig, sso } = getSSOSIGFromLocalStorage();
                const route_url = `https://unido-api.sustainable.co.ke/mainapp/discourse/sso/?sso=${sso}&sig=${sig}&token=${response.user_data.token}`;
                console.log('Navigating to route...');
                window.location.href = route_url;
                setDiscourseEntryPoint(false);
            } else {
                window.location.href = '/vetting';
            }
        } catch (error) {
            console.error('Login error:', error);
            
            // Handle network errors
            if (!error.status) {
                const networkError = new Error('Unable to connect to the server. Please check your internet connection and try again.');
                networkError.isNetworkError = true;
                throw networkError;
            }
            
            // Re-throw the enhanced error for component to handle
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const userSignUp = async (signupData) => {
        setLoading(true);
        const SIGN_UP_ENDPOINT = 'https://unido-api.sustainable.co.ke/accounts/signup/';

        try {
            const res = await fetch(SIGN_UP_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(signupData),
            });

            let response = await res.json();

            if (!res.ok) {
                setLoading(false);
                console.log('Signup Error Response', response);

                // Extract error messages from the response
                let errorMessage = '';
                if (response.error) {
                    try {
                        const errorData = JSON.parse(response.error.replace(/'/g, '"'));
                        console.log('ErrorData:', errorData);

                        if (errorData['email']) {
                            errorMessage += errorData['email'][0].string + ' ';
                        }
                        if (errorData['username']) {
                            errorMessage += errorData['username'][0].string + ' ';
                        }
                    } catch (error) {
                        console.error('Error parsing error response:', error);
                        errorMessage = 'An error occurred during signup.';
                    }
                } else {
                    errorMessage = 'An error occurred during signup.';
                }

                console.log('ErrorMessage:', errorMessage);
                toast.error(errorMessage.trim() || 'Signup Failed');
                throw new Error(errorMessage.trim());
            }

            toast.success('Signup Successful');
            
            // Store token
            if (response.user_data && response.user_data.token) {
                localStorage.setItem('token', response.user_data.token);
            }

            setUserData(response);
            setUser(response.user_data);
            setIsAuthenticated(true);
            
            // Redirect to vetting page after successful signup
            window.location.href = '/vetting';
            
            if (discourseEntryPoint) {
                const { sig, sso } = getSSOSIGFromLocalStorage();
                const route_url = `https://unido-api.sustainable.co.ke/mainapp/discourse/sso/?sso=${sso}&sig=${sig}&token=${response.user_data.token}`;
                console.log('Navigating to route...');
                window.location.href = route_url;
                setDiscourseEntryPoint(false);
            }
        } catch (error) {
            console.error('Signup error:', error);
            toast.error(error.message || 'Signup Failed');
        } finally {
            setLoading(false);
        }
    };

    const updateUserType = async (userType) => {
        setLoading(true);
    
        try {
            // Get the token from localStorage
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found. Please log in again.');
            }
    
            // Decode the token to access user_data
            const decodedToken = jwtDecode(token);
            const userToken = decodedToken.user_data.token; // Extract the shorter token
            const userId = decodedToken.user_data.user_id; // Extract the user ID from token
    
            // Log the tokens for debugging
            console.log('JWT Token:', token);
            console.log('User Token (from user_data):', userToken);
            console.log('User ID:', userId);
    
            // Make the API request with the correct token
            const response = await axios.post(
                'https://unido-api.sustainable.co.ke/mainapp/user/update-user-type/',
                { 
                    user_type: userType,
                    user_id: userId // Include the user ID in the request body
                },
                {
                    headers: {
                        'Authorization': `Token ${userToken}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
    
            if (response.data && response.data.success) {
                // Update user state
                setUser(prev => ({ ...prev, user_type: response.data.user_type }));
                setUserData(prev => ({
                    ...prev,
                    user_data: { ...prev.user_data, user_type: response.data.user_type }
                }));
    
                // Update localStorage
                const storedUserDataStr = localStorage.getItem('userData');
                if (storedUserDataStr) {
                    const storedUserData = JSON.parse(storedUserDataStr);
                    storedUserData.user_data.user_type = response.data.user_type;
                    localStorage.setItem('userData', JSON.stringify(storedUserData));
                }
    
                toast.success('Account type updated successfully');
                return response.data;
            } else {
                throw new Error(response.data?.error || 'API returned unsuccessful response');
            }
        } catch (error) {
            console.error('Error updating user type:', error);
            if (error.response?.status === 401) {
                toast.error('Your session has expired. Please log in again.');
                localStorage.removeItem('token');
                localStorage.removeItem('userData');
                setIsAuthenticated(false);
                setStartAuth(true);
                setAuthStep('login');
            } else {
                toast.error(error.message || 'Failed to update account type');
            }
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const BusinessSignUp = async (businessData) => {
        setLoading(true);
        const BUSINESS_ENDPOINT = import.meta.env.VITE_APP_BUSINESS_FORM_ENDPOINT;

        try {
            const res = await fetch(BUSINESS_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(businessData),
            });

            const response = await res.json();

            if (!res.ok || response.error) {
                throw new Error(JSON.stringify(response.error));
            }

            toast.success('Form submitted successfully!');
            window.location.href = '/vetting';
            return { status: 'success', response: response };
        } catch (error) {
            console.error('Business signup error:', error);
            toast.error('Failed to submit business information');
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const UserFormSubmission = async (submissionFormData) => {
        setLoading(true);
        try {
            const sustainability_endpoint = 'https://unido-api.sustainable.co.ke/mainapp/sustainability/';
            const res = await fetch(sustainability_endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(submissionFormData),
            });

            let response = await res.json();

            if (!res.ok) {
                throw new Error(`Form submission failed with error: ${res.status} ${res.statusText}`);
            }
            
            toast.success('Form submitted successfully');
            window.location.href = '/vetting';
        } catch (error) {
            console.error('Form submission error:', error);
            toast.error('Failed to submit form');
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const getListings = async () => {
        const LISTING_ENDPOINT = 'https://unido-api.sustainable.co.ke/mainapp/listings/';

        try {
            const res = await fetch(LISTING_ENDPOINT, { 
                method: 'GET',
                headers: {
                    'Authorization': `Token ${localStorage.getItem('token')}`
                }
            });
            const data = await res.json();
            return data;
        } catch (err) {
            console.error('Error fetching listings:', err);
            toast.error('Failed to fetch listings');
            throw err;
        }
    };

    const getResources = async () => {
        const RESOURCES_ENDPOINT = 'https://unido-api.sustainable.co.ke/mainapp/resources/';

        try {
            const res = await fetch(RESOURCES_ENDPOINT, { 
                method: 'GET',
                headers: {
                    'Authorization': `Token ${localStorage.getItem('token')}`
                }
            });
            const data = await res.json();
            return data;
        } catch (err) {
            console.error('Error fetching resources:', err);
            toast.error('Failed to fetch resources');
            throw err;
        }
    };

    const userDataUpdate = async (data) => {
        setLoading(true);
        try {
            const endpoint = `https://unido-api.sustainable.co.ke/mainapp/profile/${userData.user_data.user_id}/`;

            const res = await fetch(endpoint, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(data),
            });

            if (!res.ok) {
                throw new Error(`Update failed with error: ${res.status} ${res.statusText}`);
            }

            const response = await res.json();
            toast.success('Profile Updated Successfully');
            setUserData(response);
            setUser(response.user_data);
        } catch (err) {
            console.error('Profile update error:', err);
            toast.error('Could not update profile.');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const userLogout = () => {
        localStorage.removeItem('userData');
        localStorage.removeItem('token');
        setUserData(null);
        setUser(null);
        setIsAuthenticated(false);
        navigate('/');
    };

    useEffect(() => { 
        console.log('UserData updated:', userData); 
        console.log('User updated:', user);
        
        // Add detailed role information logging
        if (userData?.user_data) {
            console.log('Current user role:', userData.user_data.role);
            console.log('Is staff?', userData.user_data.is_staff);
            console.log('Is superuser?', userData.user_data.is_superuser);
        }
    }, [userData, user]);

    const fixUserRoles = () => {
        try {
            const storedUserData = localStorage.getItem('userData');
            if (storedUserData) {
                const parsedData = JSON.parse(storedUserData);
                console.log('Fixing userData roles:', parsedData);
                
                if (parsedData.user_data) {
                    // Set role based on superuser status
                    if (parsedData.user_data.is_superuser) {
                        console.log('Setting admin role based on is_superuser');
                        parsedData.user_data.role = 'admin';
                        
                        // Save back to localStorage
                        localStorage.setItem('userData', JSON.stringify(parsedData));
                        
                        // Update state
                        setUserData(parsedData);
                        setUser(parsedData.user_data);
                        
                        toast.success('User role updated to admin');
                        console.log('Updated userData:', parsedData);
                        return true;
                    }
                }
            }
            return false;
        } catch (error) {
            console.error('Error fixing user roles:', error);
            return false;
        }
    };

    const contextValues = {
        userData,
        setUserData,
        user,
        setUser,
        isAuthenticated,
        userSignUp,
        userLogin,
        BusinessSignUp,
        getListings,
        getResources,
        userDataUpdate,
        UserFormSubmission,
        logout: userLogout,
        userLogout,
        startAuth,
        setStartAuth,
        loading,
        discourseEntryPoint,
        setDiscourseEntryPoint,
        authStep,
        setAuthStep,
        updateUserType,
        fixUserRoles
    };

    return (
        <AppContext.Provider value={contextValues}>
            {children}
        </AppContext.Provider>
    );
};